import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Loading } from '@src/common/Loading';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import React from 'react';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useTinRegistrationStatus } from '@src/state/tinRegistrationStatus';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { MultiTinRegistrationV2 } from './MultiTinRegistrationV2';

const Card = styled('div', {
  borderRadius: 3,
  boxSizing: 'border-box',
  border: '0.0625rem solid',
  borderTop: 'none',
  backgroundColor: '$gray1',
  borderColor: '$gray4',
  boxShadow: '0 0.125rem 0.3125rem 0 rgba(0,0,0,0.05)',
});

const TextStyleBold = styled('div', {
  fontWeight: 'bold',
});

const GrayText = styled('div', {
  color: '$gray6',
});

const MarginStyle = styled('div', {
  margin: '50px 10px 10px 0.825rem',
});

const StepDescriptions = styled('div', {
  display: 'flex',
  justifyContent: 'space-around',
  marginTop: '10px',
  fontSize: '$sm',
});

const StepDescription = styled('div', {
  textAlign: 'center',
  flex: 1,
  color: '$gray6',
});

const IconContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  marginBottom: '20px',
});

const IconItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
});

const IconLabel = styled('div', {
  marginBottom: '8px',
  fontSize: '$sm',
  fontWeight: 500,
  textAlign: 'center',
  color: '$gray8',
});

export const MultiTinRegistration = ({ tinreg }) => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');

  // If feature toggle is enabled, use new flow with claimsAttribute, otherwise use old flow
  // we can remove this toggle once we go live in prod , and also delete the v2 files
  return featureToggles.getRow('ENABLE_CLAIMS_ATTRIBUTE_FLOW')?.active ? (
    <MultiTinRegistrationWithClaimsAttribute tinreg={tinreg} />
  ) : (
    <MultiTinRegistrationV2 tinreg={tinreg} />
  );
};

const MultiTinRegistrationWithClaimsAttribute = ({ tinreg }) => {
  const { searchRegistrationStatusSaga } = useTinRegistrationStatus.state();

  const {
    registrationStatus,
    trackingNumber,
    reviewCompletedOn,
    submittedOn,
    claimsAttribute,
  } = tinreg;

  return (
    <Loading isLoading={searchRegistrationStatusSaga.isLoading}>
      <div id="multi-tin-print-status">
        <Card>
          <Grid>
            <Grid.Col span="17%">
              <MarginStyle>
                <GrayText id="Status-ReferenceNumberText">
                  Reference number
                </GrayText>
                <TextStyleBold id="Status-ReferenceNumber">
                  {trackingNumber}
                </TextStyleBold>
              </MarginStyle>
              <MarginStyle>
                <GrayText id="Status-SubDateText">Submission date</GrayText>
                <TextStyleBold id="Status-SubDate">{submittedOn}</TextStyleBold>
              </MarginStyle>
            </Grid.Col>
            <Grid.Col span="2%">
              <Divider orientation="vertical" />
            </Grid.Col>
            <Grid.Col
              span="80%"
              style={{ paddingTop: '1em', paddingBottom: '1em' }}
            >
              <IconContainer>
                <IconItem>
                  <IconLabel>Submission</IconLabel>
                  <IconSymbol
                    icon="check_circle"
                    size="32px"
                    color="$success1"
                    variant="outlined"
                  />
                </IconItem>
                <IconItem>
                  <IconLabel>Submission Review</IconLabel>
                  {registrationStatus === 'PENDING' ? (
                    <IconSymbol icon="pending" size="32px" color="$info1" />
                  ) : (
                    <IconSymbol
                      icon="check_circle"
                      size="32px"
                      color="$success1"
                      variant="outlined"
                    />
                  )}
                </IconItem>
                <IconItem>
                  <IconLabel>
                    {claimsAttribute ? 'Claim Submitted' : 'Claim Submission'}
                  </IconLabel>
                  {claimsAttribute ||
                  (registrationStatus === 'ACCEPTED' && !claimsAttribute) ||
                  (registrationStatus === 'DENIED' && !claimsAttribute) ? (
                    <IconSymbol
                      icon="check_circle"
                      size="32px"
                      color="$success1"
                      variant="outlined"
                    />
                  ) : registrationStatus === 'RELEASED' ? (
                    <IconSymbol
                      icon="description"
                      size="32px"
                      color="$warning1"
                    />
                  ) : (
                    <IconSymbol icon="description" size="32px" color="$gray6" />
                  )}
                </IconItem>
                <IconItem>
                  <IconLabel>Verification</IconLabel>
                  {(registrationStatus === 'RELEASED' && claimsAttribute) ||
                  (registrationStatus === 'ACCEPTED' && claimsAttribute) ||
                  (registrationStatus === 'ACCEPTED' && !claimsAttribute) ? (
                    <IconSymbol icon="verified" size="32px" color="$success1" />
                  ) : registrationStatus === 'DENIED' ? (
                    <IconSymbol icon="cancel" size="32px" color="$error1" />
                  ) : (
                    <IconSymbol
                      icon="lock"
                      size="32px"
                      variant="outlined"
                      color="$gray6"
                    />
                  )}
                </IconItem>
              </IconContainer>
              <StepDescriptions>
                <StepDescription css={{ color: '$success1' }}>
                  Complete
                </StepDescription>
                <StepDescription
                  css={{
                    color:
                      registrationStatus === 'PENDING' ? '$info1' : '$success1',
                  }}
                >
                  {registrationStatus === 'PENDING' ? (
                    <React.Fragment>
                      In progress
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        You will receive an email once the review is complete
                      </StepDescription>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      Complete
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        Completed on {reviewCompletedOn || ''}
                      </StepDescription>
                    </React.Fragment>
                  )}
                </StepDescription>
                <StepDescription
                  css={{
                    color:
                      claimsAttribute ||
                      (registrationStatus === 'ACCEPTED' && !claimsAttribute) ||
                      (registrationStatus === 'DENIED' && !claimsAttribute)
                        ? '$success1'
                        : registrationStatus === 'RELEASED'
                        ? '$warning1'
                        : '$gray6',
                  }}
                >
                  {claimsAttribute ||
                  (registrationStatus === 'ACCEPTED' && !claimsAttribute) ||
                  (registrationStatus === 'DENIED' && !claimsAttribute)
                    ? 'Complete'
                    : 'Required'}
                </StepDescription>
                <StepDescription
                  css={{
                    color:
                      registrationStatus === 'DENIED'
                        ? '$error1'
                        : (registrationStatus === 'ACCEPTED' &&
                            claimsAttribute) ||
                          (registrationStatus === 'ACCEPTED' &&
                            !claimsAttribute)
                        ? '$success1'
                        : registrationStatus === 'RELEASED' && claimsAttribute
                        ? '$success1'
                        : '$gray6',
                  }}
                >
                  {registrationStatus === 'DENIED' ? (
                    <React.Fragment>
                      Denied
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        This TIN cannot be validated at this time
                      </StepDescription>
                    </React.Fragment>
                  ) : (registrationStatus === 'ACCEPTED' && claimsAttribute) ||
                    (registrationStatus === 'ACCEPTED' && !claimsAttribute) ? (
                    <React.Fragment>
                      Approved
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        The TIN has been successfully verified
                      </StepDescription>
                    </React.Fragment>
                  ) : registrationStatus === 'RELEASED' && claimsAttribute ? (
                    <React.Fragment>
                      Approved
                      <br />
                      <StepDescription css={{ color: '$gray6' }}>
                        The TIN has been successfully verified
                      </StepDescription>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>Pending</React.Fragment>
                  )}
                </StepDescription>
              </StepDescriptions>
            </Grid.Col>
          </Grid>
        </Card>
      </div>
    </Loading>
  );
};

MultiTinRegistrationWithClaimsAttribute.propTypes = {
  tinreg: PropTypes.shape([]).isRequired,
};
MultiTinRegistration.propTypes = {
  tinreg: PropTypes.shape([]).isRequired,
};
